import React, { forwardRef } from "react";
import {
  FLOW_BUTTON_SOLID_SHELL,
  FLOW_FORM_FIELD_LABEL_BASE,
  FLOW_FORM_HELPER_TEXT_BASE,
  FLOW_FORM_NATIVE_CONTROL_ACCENT_CLASS,
  FLOW_SECONDARY_BUTTON_BASE,
  FLOW_TEXT_INPUT_BASE,
  FLOW_TEXTAREA_BASE,
} from "../../../styles/twDesignPrimitives";
import { cn } from "../../../utils/cn";

export type StyleGuideButtonVariant = "save" | "action" | "nav";
export type StyleGuideControlSize = "sm" | "md" | "lg";

const BUTTON_SOLID_SKIN: Record<StyleGuideButtonVariant, string> = {
  save: "bg-[#38A169] text-white hover:bg-[#2F855A] focus-visible:ring-[#38A169]/35",
  action: "bg-[#DD6435] text-white hover:bg-[#C05621] focus-visible:ring-[#DD6435]/35",
  nav: "bg-[#3182CE] text-white hover:bg-[#2B6CB0] focus-visible:ring-[#3182CE]/35",
};

const BUTTON_SIZE_CLASS: Record<StyleGuideControlSize, string> = {
  sm: "min-h-8 px-3 py-1.5 text-xs",
  md: "min-h-9 px-4 py-2 text-sm",
  lg: "min-h-11 px-5 py-2.5 text-base",
};

export type StyleGuideButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: StyleGuideButtonVariant;
  size?: StyleGuideControlSize;
  loading?: boolean;
};

export const StyleGuideButton = forwardRef<HTMLButtonElement, StyleGuideButtonProps>(
  function StyleGuideButton(
    { variant = "save", size = "md", loading = false, className, children, disabled, ...rest },
    ref
  ) {
    return (
      <button
        ref={ref}
        type="button"
        className={cn(FLOW_BUTTON_SOLID_SHELL, BUTTON_SOLID_SKIN[variant], BUTTON_SIZE_CLASS[size], className)}
        disabled={disabled || loading}
        {...rest}
      >
        {loading ? (
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" aria-hidden />
        ) : (
          children
        )}
      </button>
    );
  }
);

export type StyleGuideInputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  helperText?: string;
  validationState?: "default" | "error" | "success";
  leftIcon?: React.ReactNode;
  fieldSize?: StyleGuideControlSize;
};

const FIELD_SIZE_PADDING: Record<StyleGuideControlSize, string> = {
  sm: "px-2.5 py-1.5 text-xs min-h-8",
  md: "px-3 py-2 text-sm min-h-9",
  lg: "px-3.5 py-2.5 text-base min-h-11",
};

const validationRing: Record<NonNullable<StyleGuideInputProps["validationState"]>, string> = {
  default: "",
  error: "border-[#E53E3E] ring-2 ring-[#E53E3E]/25 focus:border-[#E53E3E] focus:ring-[#E53E3E]/25",
  success: "border-[#38A169] ring-2 ring-[#38A169]/20 focus:border-[#38A169] focus:ring-[#38A169]/20",
};

export const StyleGuideInput: React.FC<StyleGuideInputProps> = ({
  label,
  helperText,
  validationState = "default",
  leftIcon,
  fieldSize = "md",
  className,
  id,
  ...rest
}) => {
  const inputId = id ?? rest.name ?? "style-guide-input";
  return (
    <div className="w-full max-w-md">
      <label htmlFor={inputId} className={FLOW_FORM_FIELD_LABEL_BASE}>
        {label}
      </label>
      <div className={cn("relative mt-1", leftIcon ? "isolate" : undefined)}>
        {leftIcon ? (
          <span className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-[#6B7280]">
            {leftIcon}
          </span>
        ) : null}
        <input
          id={inputId}
          className={cn(
            FLOW_TEXT_INPUT_BASE,
            FIELD_SIZE_PADDING[fieldSize],
            validationState !== "default" && validationRing[validationState],
            leftIcon ? "pl-9" : undefined,
            className
          )}
          aria-invalid={validationState === "error"}
          {...rest}
        />
      </div>
      {helperText ? (
        <p className={cn(FLOW_FORM_HELPER_TEXT_BASE, validationState === "error" && "text-[#E53E3E]")}>
          {helperText}
        </p>
      ) : null}
    </div>
  );
};

export type StyleGuideTextareaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement> & {
  label: string;
  helperText?: string;
  validationState?: "default" | "error" | "success";
  fieldSize?: StyleGuideControlSize;
};

export const StyleGuideTextarea: React.FC<StyleGuideTextareaProps> = ({
  label,
  helperText,
  validationState = "default",
  fieldSize = "md",
  className,
  id,
  ...rest
}) => {
  const inputId = id ?? rest.name ?? "style-guide-textarea";
  return (
    <div className="w-full max-w-md">
      <label htmlFor={inputId} className={FLOW_FORM_FIELD_LABEL_BASE}>
        {label}
      </label>
      <textarea
        id={inputId}
        className={cn(
          FLOW_TEXTAREA_BASE,
          FIELD_SIZE_PADDING[fieldSize],
          validationState !== "default" && validationRing[validationState],
          className
        )}
        aria-invalid={validationState === "error"}
        {...rest}
      />
      {helperText ? (
        <p className={cn(FLOW_FORM_HELPER_TEXT_BASE, validationState === "error" && "text-[#E53E3E]")}>
          {helperText}
        </p>
      ) : null}
    </div>
  );
};

export type StyleGuideCardVariant = "default" | "elevated" | "ghost";
export type StyleGuideCardPadding = "sm" | "md" | "lg";

const CARD_PADDING: Record<StyleGuideCardPadding, string> = {
  sm: "p-3",
  md: "p-4",
  lg: "p-6",
};

export type StyleGuideCardProps = {
  variant?: StyleGuideCardVariant;
  padding?: StyleGuideCardPadding;
  children: React.ReactNode;
  className?: string;
};

export const StyleGuideCard: React.FC<StyleGuideCardProps> = ({
  variant = "default",
  padding = "md",
  children,
  className,
}) => {
  return (
    <div
      className={cn(
        "rounded-lg border border-solid",
        variant === "default" && "border-[#E2E8F0] bg-white",
        variant === "elevated" && "border-transparent bg-white shadow-md",
        variant === "ghost" && "border-transparent bg-transparent shadow-none",
        CARD_PADDING[padding],
        className
      )}
    >
      {children}
    </div>
  );
};

export type StyleGuideBadgeVariant = "primary" | "secondary" | "success" | "error" | "ghost";
export type StyleGuideBadgeSize = "sm" | "md";

const BADGE_VARIANT: Record<StyleGuideBadgeVariant, string> = {
  primary: "bg-[#3182CE] text-white",
  secondary: "bg-slate-100 text-[#2D3748]",
  success: "bg-[#C6F6D5] text-[#22543D]",
  error: "bg-[#FED7D7] text-[#822727]",
  ghost: "border border-[#E2E8F0] bg-white text-[#4A5568]",
};

const BADGE_SIZE: Record<StyleGuideBadgeSize, string> = {
  sm: "px-2 py-0.5 text-[10px] uppercase tracking-wide",
  md: "px-2.5 py-1 text-xs font-semibold",
};

export type StyleGuideBadgeProps = {
  variant?: StyleGuideBadgeVariant;
  size?: StyleGuideBadgeSize;
  children: React.ReactNode;
  className?: string;
};

export const StyleGuideBadge: React.FC<StyleGuideBadgeProps> = ({
  variant = "primary",
  size = "md",
  children,
  className,
}) => (
  <span
    className={cn(
      "inline-flex items-center rounded-full font-sans font-medium leading-none",
      BADGE_VARIANT[variant],
      BADGE_SIZE[size],
      className
    )}
  >
    {children}
  </span>
);

export const StyleGuideNativeCheckboxDemo: React.FC = () => (
  <div className="w-full max-w-full min-w-0 space-y-2">
    <p className={FLOW_FORM_FIELD_LABEL_BASE}>Checkbox</p>
    <label className="flex w-full max-w-full min-w-0 items-center gap-2 text-sm text-[#2D3748] sm:inline-flex sm:w-auto">
      <input type="checkbox" className={cn("h-4 w-4 shrink-0 rounded border-[#CBD5E0]", FLOW_FORM_NATIVE_CONTROL_ACCENT_CLASS)} />
      Remember me
    </label>
  </div>
);

export const StyleGuideNativeRadioDemo: React.FC = () => (
  <fieldset className="w-full max-w-full min-w-0 space-y-2 border-0 p-0">
    <legend className={cn(FLOW_FORM_FIELD_LABEL_BASE, "px-0")}>Radius</legend>
    <label className="flex items-center gap-2 text-sm text-[#2D3748]">
      <input
        type="radio"
        name="style-guide-radius"
        defaultChecked
        className={cn("h-4 w-4 border-[#CBD5E0]", FLOW_FORM_NATIVE_CONTROL_ACCENT_CLASS)}
      />
      sm
    </label>
    <label className="flex items-center gap-2 text-sm text-[#2D3748]">
      <input
        type="radio"
        name="style-guide-radius"
        className={cn("h-4 w-4 border-[#CBD5E0]", FLOW_FORM_NATIVE_CONTROL_ACCENT_CLASS)}
      />
      lg
    </label>
  </fieldset>
);

export const StyleGuideSecondaryButtonShell: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement>> = ({
  className,
  children,
  ...rest
}) => (
  <button type="button" className={cn(FLOW_SECONDARY_BUTTON_BASE, BUTTON_SIZE_CLASS.md, className)} {...rest}>
    {children}
  </button>
);

type ProductButtonSize = "small" | "medium" | "large";
type ProductButtonVariant = "fill" | "outline";

const PRODUCT_BUTTON_SIZE_CLASS: Record<ProductButtonSize, string> = {
  small: "text-[0.875rem] px-2 py-2 [&_svg]:h-4 [&_svg]:w-4",
  medium: "text-base px-2.5 py-2.5 [&_svg]:h-4 [&_svg]:w-4",
  large: "text-base px-3.5 py-3.5 [&_svg]:h-5 [&_svg]:w-5",
};

function ProductButton({
  variant,
  size,
  colorHex,
  children,
}: {
  variant: ProductButtonVariant;
  size: ProductButtonSize;
  colorHex?: string;
  children: React.ReactNode;
}) {
  const defaultFillHex = "#3182CE";
  const resolvedColor = colorHex ?? defaultFillHex;

  return (
    <button
      type="button"
      className={cn(
        "flex w-full items-center justify-center gap-2 rounded-lg font-semibold",
        "border-2 border-solid font-['Inter',sans-serif] transition-colors duration-150",
        PRODUCT_BUTTON_SIZE_CLASS[size],
        variant === "fill" && "border-transparent text-white",
        variant === "outline" && "bg-transparent text-[#3182CE] border-current"
      )}
      style={variant === "fill" ? { backgroundColor: resolvedColor } : undefined}
    >
      {children}
    </button>
  );
}

export const CustomButtonMatrix: React.FC<{ orangeHex: string }> = ({ orangeHex }) => (
  <table className="w-full border-collapse text-left text-sm">
    <thead>
      <tr className="border-b border-[#E2E8F0] text-[#718096]">
        <th className="py-2 pr-4 font-medium">Size</th>
        <th className="py-2 pr-4 font-medium">Save</th>
        <th className="py-2 pr-4 font-medium">Action</th>
        <th className="py-2 font-medium">Nav</th>
      </tr>
    </thead>
    <tbody className="text-[#2D3748]">
      {(
        [
          ["small", "small"],
          ["medium", "medium"],
          ["large", "large"],
        ] as const
      ).map(([label, size]) => (
        <tr key={label} className="border-b border-[#F1F5F9] align-middle">
          <td className="py-3 pr-4 font-mono text-xs">{label}</td>
          <td className="py-3 pr-4">
            <ProductButton variant="fill" size={size}>
              Save
            </ProductButton>
          </td>
          <td className="py-3 pr-4">
            <ProductButton variant="fill" size={size} colorHex={orangeHex}>
              Publish
            </ProductButton>
          </td>
          <td className="py-3">
            <ProductButton variant="outline" size={size}>
              Back
            </ProductButton>
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

export const StyleGuideFlowButtonRow: React.FC = () => (
  <div className="flex flex-wrap gap-3">
    <StyleGuideButton variant="save" size="md">
      Save
    </StyleGuideButton>
    <StyleGuideButton variant="action" size="md">
      Publish
    </StyleGuideButton>
    <StyleGuideButton variant="nav" size="md">
      Continue
    </StyleGuideButton>
    <StyleGuideSecondaryButtonShell>Secondary</StyleGuideSecondaryButtonShell>
  </div>
);
